import { getQueryString } from '@/components/filters';
import { cn } from '@/lib/utils';
import { VisitHelperOptions } from '@inertiajs/core';
import { router } from '@inertiajs/react';
import { Loader2, Search, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { route } from 'ziggy-js';

interface SearchInputProps {
    placeholder?: string;
    className?: string;
    debounceMs?: number;
    minLength?: number;
    autoFocusOnExternalChange?: boolean;
}

// Конфигурация по умолчанию
const DEFAULT_DEBOUNCE_MS = 500;
const DEFAULT_MIN_LENGTH = 3;

export function SearchInput({
    placeholder,
    className,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    minLength = DEFAULT_MIN_LENGTH,
    autoFocusOnExternalChange = true,
}: SearchInputProps) {
    const qp = route().queryParams;

    const params = (qp ?? {}) as { search?: string };
    const { search } = params;
    const currentSearch = getQueryString(search, '') as string;

    const [isSearching, setIsSearching] = useState(false);
    const [displayValue, setDisplayValue] = useState(currentSearch);
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Мемоизируем параметры роутера для избежания пересоздания
    const routerOptions = useMemo(
        (): VisitHelperOptions => ({
            preserveState: true,
            only: ['products'],
            reset: ['products'],
            onStart: () => setIsSearching(true),
            onFinish: () => setIsSearching(false),
        }),
        [],
    );

    // Очистка таймера при размонтировании компонента
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    // Синхронизация внутреннего состояния при изменении внешнего значения
    useEffect(() => {
        // Опциональный автофокус (отключен по умолчанию для лучшего UX)
        if (currentSearch && autoFocusOnExternalChange && inputRef.current) {
            inputRef.current.focus();
        }
    }, [currentSearch, autoFocusOnExternalChange]);

    // Универсальная функция для навигации с поиском
    const navigateWithSearch = useCallback(
        (searchValue: string | null) => {
            const data = { ...qp };

            if (searchValue === null) {
                delete data.search;
            } else {
                data.search = searchValue;
            }

            router.get(route('home'), data, routerOptions);
        },
        [qp, routerOptions],
    );

    const handleInputChange = useCallback(
        (newValue: string) => {
            setDisplayValue(newValue);

            // Очистка существующего таймера
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            // Установка нового таймера для debounce
            debounceTimerRef.current = setTimeout(() => {
                if (newValue.length === 0) {
                    navigateWithSearch(null);
                } else if (newValue.length >= minLength) {
                    navigateWithSearch(newValue);
                }
                // Если 1-2 символа - не выполняем поиск (ничего не делаем)
            }, debounceMs);
        },
        [debounceMs, minLength, navigateWithSearch],
    );

    const handleClear = useCallback(() => {
        // Очистка таймера и состояния
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        setDisplayValue('');
        navigateWithSearch(null);

        // Возвращаем фокус на input для удобства пользователя
        inputRef.current?.focus();
    }, [navigateWithSearch]);

    // Показывать подсказку о минимальной длине
    const shouldShowMinLengthHint =
        displayValue.length > 0 && displayValue.length < minLength;

    return (
        <div className={cn('relative', className)}>
            {/* Иконка поиска/загрузки */}
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                {isSearching ? (
                    <Loader2
                        className="h-5 w-5 animate-spin text-brand-gold"
                        aria-hidden="true"
                    />
                ) : (
                    <Search
                        className="h-5 w-5 text-zinc-500 transition-colors"
                        aria-hidden="true"
                    />
                )}
            </div>

            {/* Поле ввода */}
            <input
                type="text"
                ref={inputRef}
                className={cn(
                    'block h-10 w-full rounded-lg border border-zinc-800 bg-zinc-900 pl-10 text-sm text-brand-mist placeholder-zinc-500 transition-all focus:border-brand-gold focus:outline-none focus:ring-brand-gold',
                    displayValue ? 'pr-10' : 'pr-4',
                    isSearching && 'border-brand-gold/30',
                )}
                placeholder={placeholder}
                value={displayValue}
                onChange={(e) => handleInputChange(e.target.value)}
                aria-label={placeholder || 'Поиск по каталогу'}
                aria-describedby={
                    shouldShowMinLengthHint ? 'search-hint' : undefined
                }
            />

            {/* Кнопка сброса/очистки */}
            {displayValue && (
                <button
                    type="button"
                    onClick={handleClear}
                    className="animate-in fade-in zoom-in-90 absolute inset-y-0 right-0 flex items-center rounded pr-3 text-zinc-500 transition-colors duration-200 hover:text-brand-gold focus:outline-none focus:ring-2 focus:ring-brand-gold focus:ring-offset-2 focus:ring-offset-zinc-900"
                    aria-label="Очистить поиск"
                >
                    <X className="h-5 w-5" aria-hidden="true" />
                </button>
            )}

            {/* Визуальная подсказка о минимальной длине */}
            {shouldShowMinLengthHint && (
                <div
                    id="search-hint"
                    className="animate-in fade-in slide-in-from-top-1 absolute -bottom-5 left-0 text-[10px] text-zinc-600"
                    role="status"
                    aria-live="polite"
                >
                    Минимум {minLength} символа для поиска
                </div>
            )}
        </div>
    );
}
